import { Suspense, lazy, useEffect, useState, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChunkBoundary } from "../common/ChunkBoundary.js";
import { classifyChunkFailure, currentEntryScript, fetchServedIndex, type ChunkFailureKind } from "./chunkFailure.js";
import type { Tab } from "./navigation.js";
import { finishStartup, startupError, startupRouteReady } from "../../lib/startupTelemetry.js";
import { RouteLoadingBoundary, ViewFallback } from "./RouteLoadingBoundary.js";
export { ViewFallback } from "./RouteLoadingBoundary.js";

/**
 * One lazy chunk per tab (#1678).
 *
 * `App.tsx` used to import all nineteen views statically, so a Dashboard open
 * on a phone downloaded the Home Map editor, the finance charts, the assistant
 * transcript, the network scanner and the garden planner in one 2.3 MB chunk.
 * Each view is now a dynamic import behind `Suspense`, so a tab costs its own
 * chunk and the shell chunk holds the shell.
 *
 * Declared beside the tab registry rather than in `App.tsx`, which must not
 * grow (#1682). The route is parsed by the shell before any chunk lands —
 * `currentRoute()` runs in the main chunk on mount — so a cold deep link
 * (`/inventory/assets/:id`, `/recipes/:id`, `/shopping?listId=`) resolves to
 * its tab and object first and waits for the chunk second.
 *
 * After startup the service worker warms reachable chunks with bounded
 * concurrency on unconstrained links. Used views are cached on demand, and
 * speculative locale chunks remain excluded.
 */
// `any` here is React's own constraint on `lazy` (ComponentType<any>): the
// registry keeps each view's real prop type through `M[K]`, so a call site
// still fails to compile on a wrong prop.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

function lazyView<M extends Record<K, AnyComponent>, K extends string>(load: () => Promise<M>, name: K): LazyExoticComponent<M[K]> & { preload: () => Promise<M> } {
  let pending: Promise<M> | undefined;
  const preload = () => pending ??= load();
  return Object.assign(lazy(async () => ({ default: (await preload())[name] })), { preload });
}

// These are the namespaces the active views already request. Starting them
// alongside the JS avoids serial downloads without loading off-route bundles.
export const viewNamespaces = {
  dashboard: "insights", assistant: "assistant", search: "insights", scan: "devices",
  inventory: "inventory", groceries: "groceries", chores: "chores", projects: "projects",
  "floor-plan": "floorPlan", garden: "garden", bms: "devices", network: "devices",
  recipes: "recipes", "meal-assembler": "meals", shopping: "shopping", feedback: "insights",
  finance: "finance", "release-notes": "devices", history: "insights", settings: "settings"
} satisfies Record<Tab, string>;

export const views = {
  dashboard: lazyView(() => import("../dashboard/DashboardView.js"), "DashboardView"),
  assistant: lazyView(() => import("../assistant/index.js"), "AssistantView"),
  search: lazyView(() => import("../search/SearchView.js"), "SearchView"),
  scan: lazyView(() => import("../scan/ScanView.js"), "ScanView"),
  inventory: lazyView(() => import("../inventory/index.js"), "InventoryView"),
  groceries: lazyView(() => import("../groceries/GroceriesTab.js"), "GroceriesTab"),
  chores: lazyView(() => import("../chores/index.js"), "ChoresView"),
  projects: lazyView(() => import("../projects/ProjectsView.js"), "ProjectsView"),
  "floor-plan": lazyView(() => import("../floorPlan/index.js"), "FloorPlanView"),
  garden: lazyView(() => import("../garden/GardenView.js"), "GardenView"),
  bms: lazyView(() => import("../bms/BmsView.js"), "BmsView"),
  network: lazyView(() => import("../network/NetworkView.js"), "NetworkView"),
  recipes: lazyView(() => import("../recipes/RecipesView.js"), "RecipesView"),
  "meal-assembler": lazyView(() => import("../meals/MealsView.js"), "MealsView"),
  shopping: lazyView(() => import("../shopping/index.js"), "ShoppingView"),
  feedback: lazyView(() => import("../feedback/FeedbackView.js"), "FeedbackView"),
  finance: lazyView(() => import("../finance/FinanceView.js"), "FinanceView"),
  "release-notes": lazyView(() => import("../releases/ReleaseNotesView.js"), "ReleaseNotesView"),
  history: lazyView(() => import("../history/HistoryView.js"), "HistoryView"),
  settings: lazyView(() => import("../settings/SettingsView.js"), "SettingsView")
} satisfies Record<Tab, unknown>;

/** The public pages, their own chunk: a signed-out visitor never downloads the app. */
export const MarketingSiteLazy = lazyView(() => import("../marketing/MarketingSite.js"), "MarketingSite");

function browserOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

/**
 * A view that did not arrive (#997, #1678; reworded in the September audit).
 *
 * The one sentence this used to show — "A new version may have been
 * installed since this tab was opened" — was given for every failure, and
 * offline it stacked under two banners that said something else. The cases
 * are told apart in `chunkFailure.ts`: a lost connection, a deploy under this
 * tab (proved by re-reading index.html, not guessed), a request that did not
 * get through, and a plain render error. The action is a reload in every
 * case, because `React.lazy` remembers a rejected import and the module map
 * remembers a failed fetch; only the words and the button's label change.
 */
function ChunkFailure({ error }: { error: unknown }) {
  useEffect(() => { startupError("route_failed"); finishStartup("error"); }, []);
  const { t } = useTranslation("shell");
  const [kind, setKind] = useState<ChunkFailureKind | "checking">(() => {
    const quick = classifyChunkFailure({ error, online: browserOnline(), servedIndex: null, entryScript: null });
    // Online with a chunk error: "updated" is claimed only on evidence, and
    // that takes one request.
    return quick === "network" ? "checking" : quick;
  });

  useEffect(() => {
    if (kind !== "checking") return undefined;
    let cancelled = false;
    void fetchServedIndex().then((servedIndex) => {
      if (cancelled) return;
      setKind(classifyChunkFailure({ error, online: browserOnline(), servedIndex, entryScript: currentEntryScript() }));
    });
    return () => {
      cancelled = true;
    };
  }, [error, kind]);

  const resolved: ChunkFailureKind = kind === "checking" ? "network" : kind;
  return (
    <section className="panel route-not-found" role="alert">
      <p>{t(`chunkFailed.${resolved}`)}</p>
      <p>
        <button type="button" onClick={() => window.location.reload()}>
          {resolved === "updated" || resolved === "crash" ? t("chunkFailed.reload") : t("chunkFailed.retry")}
        </button>
      </p>
    </section>
  );
}

function RouteCommitted() {
  useEffect(() => { startupRouteReady(); }, []);
  return null;
}

export function ViewSuspense({ children, tab }: { children: ReactNode; tab?: Tab }) {
  const view = tab ? views[tab] : MarketingSiteLazy;
  return (
    <ChunkBoundary failure={(error) => <ChunkFailure error={error} />}>
      <RouteLoadingBoundary namespace={tab ? viewNamespaces[tab] : "devices"} preload={view.preload}>
        <Suspense fallback={<ViewFallback />}>{children}<RouteCommitted /></Suspense>
      </RouteLoadingBoundary>
    </ChunkBoundary>
  );
}

